import { useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import connectionMonitor from "../../../utils/connectionMonitor";
import { getAuthToken } from "../../../utils/getAuthToken";
import { useContactSelection } from "../../../hooks/useContactSelection";
import useSfuCallConnection from "./useSfuCallConnection";
import {
  callIncoming,
  focusIncomingCall,
  clearIncoming,
  callStarted,
  callConnected,
  callStatusUpdate,
  callEnded,
  callError,
  markAnswerable,
  unmarkAnswerable,
  transferIncoming,
  clearIncomingTransfer,
  transferPendingStarted,
  transferPendingCleared,
} from "../../../redux/Slice/Calls/callSlice";
import { patchChatMessage } from "../../../redux/Slice/Inbox/InboxMessageSlice";

// Calling has its own dedicated single-instance service — falls back to the
// main API server only if REACT_APP_CALLING_URL is unset (e.g. not yet configured).
const SERVER_DOMAIN = process.env.REACT_APP_CALLING_URL || process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

async function authPost(path, body) {
  const token = await getAuthToken();
  const res = await axios.post(`${SERVER_DOMAIN}${path}`, body, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return res.data;
}

async function authGet(path) {
  const token = await getAuthToken();
  const res = await axios.get(`${SERVER_DOMAIN}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

// Regular axios/fetch calls can get cancelled mid-flight when the page is
// actually unloading — fetch's `keepalive` flag exists specifically so a
// request survives that. sendBeacon doesn't support custom headers, so it
// can't carry the Bearer token; this is the correct modern equivalent.
async function beaconHangup(callId) {
  const token = await getAuthToken();
  try {
    fetch(`${SERVER_DOMAIN}/api/calling/hangup`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ callId }),
      keepalive: true,
    });
  } catch (e) {
    // best-effort — nothing to recover into if this fails, the page is closing
  }
}

// Translates what actually failed into something an agent can act on —
// "timeout of 10000ms exceeded" or a raw NotAllowedError name means nothing
// to someone deciding whether to just try again.
function describeCallError(err) {
  if (err.name === "NotAllowedError") return "Microphone access is blocked — allow it in your browser and try again";
  if (err.response?.status === 409) return "This call was already answered by another agent";
  if (err.code === "ECONNABORTED" || err.response?.data?.error?.includes("timeout")) {
    return "Connecting to WhatsApp timed out — please try again";
  }
  // Backend already sends a clean, actionable message for this (see
  // calling-server/controllers/callingController.js's sendCallingError) —
  // called out explicitly here, same as dialOut's NO_CALL_PERMISSION check
  // below, so the mapping is obvious in code rather than relying on the
  // generic fallback happening to carry the right string through.
  if (err.response?.data?.code === "CALLING_NOT_CONFIGURED") return err.response.data.error;
  return err.response?.data?.error || err.message || "Something went wrong with this call";
}

function waitForIceGatheringComplete(pc) {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const timeout = setTimeout(resolve, 4000); // safety valve, proceed with whatever ICE candidates gathered so far
    pc.addEventListener("icegatheringstatechange", function check() {
      if (pc.iceGatheringState === "complete") {
        clearTimeout(timeout);
        pc.removeEventListener("icegatheringstatechange", check);
        resolve();
      }
    });
  });
}

/**
 * Owns the RTCPeerConnection lifecycle end to end. Mount exactly once
 * (in CallManager, near the app root) — not per-conversation — so a call
 * survives navigating around the inbox. Signaling is relayed through
 * connectionMonitor's socket bridge; media is a direct browser<->Meta
 * WebRTC connection this hook never proxies.
 */
export default function useCallConnection() {
  const dispatch = useDispatch();
  // Sourced from Redux, not useParams — CallManager (which owns this hook)
  // is mounted at the app root, outside the /home/:accountid route tree, so
  // route params aren't available here.
  const authUser = useSelector((state) => state.auth?.user);
  const authUserRef = useRef(authUser);
  authUserRef.current = authUser;
  // selectContact is the ONLY thing that actually opens a chat — it dispatches
  // setselectedContactID/fetchMessagesByContact/etc, which is what ChatBody's
  // own effect watches. A plain navigate() only changes the URL and silently
  // renders nothing, because ChatBody drives off Redux state, not the route.
  // useContactSelection's own useParams() returns empty here (CallManager
  // isn't under a matched Route), which is fine — accountid/userid are passed
  // explicitly below, sourced from Redux instead.
  const { selectContact } = useContactSelection();
  const { joinSfuCall, leaveSfuCall } = useSfuCallConnection();
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioElRef = useRef(null);
  // Keyed by callId, not a single object — a business line can ring more
  // than one call at once (two different customers calling in together),
  // and each one needs its own offerSdp available to answer/reject later,
  // independent of whichever call currently occupies the corner widget's
  // single display slot (state.calls.incoming). Answering/rejecting from the
  // Calls sidebar looks a call up here by its own callId, never "whatever
  // rang most recently" — that overwrite is exactly what silently answered
  // the wrong call before this was a Map.
  const pendingIncomingCallsRef = useRef(new Map());
  // Tracks the call this tab currently owns (ringing→connected), purely so
  // the beforeunload handler knows whether there's anything to clean up —
  // not used for signaling logic.
  const activeCallIdRef = useRef(null);

  // Opens the caller's conversation the moment a call connects, so the agent
  // can read chat history while talking.
  const navigateToContactChat = useCallback(
    (contactId) => {
      if (!contactId) return;
      const user = authUserRef.current;
      const accountId = user?.accountId || user?.AccountID;
      const userId = user?._id || user?.userId;
      if (!accountId || !userId) return;
      selectContact(contactId, { navigate: true, accountid: accountId, userid: userId, clearPrevious: true });
    },
    [selectContact]
  );

  const ensureRemoteAudioEl = () => {
    if (!remoteAudioElRef.current) {
      const el = document.createElement("audio");
      el.autoplay = true;
      document.body.appendChild(el);
      remoteAudioElRef.current = el;
    }
    return remoteAudioElRef.current;
  };

  const teardown = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (remoteAudioElRef.current) {
      remoteAudioElRef.current.srcObject = null;
    }
    // Harmless no-op for a direct-mode call — nothing was ever joined via
    // mediasoup, so every ref inside is already null. Unconditional on
    // purpose: it means this function doesn't need to know which mode the
    // call it's tearing down was in.
    leaveSfuCall();
    // Deliberately does NOT touch pendingIncomingCallsRef — teardown is about
    // this tab's own active/attempted call, and other calls may still be
    // legitimately ringing and answerable independent of it.
    activeCallIdRef.current = null;
  }, [leaveSfuCall]);

  const newPeerConnection = useCallback(() => {
    // No STUN/TURN configured — same as the Step 0 harness, this only works
    // if the earlier finding (no relay needed to reach Meta) holds generally.
    const pc = new RTCPeerConnection({ iceServers: [] });
    pc.ontrack = (event) => {
      ensureRemoteAudioEl().srcObject = event.streams[0];
    };
    pc.onconnectionstatechange = () => {
      if (["failed", "closed"].includes(pc.connectionState)) {
        dispatch(callEnded(activeCallIdRef.current));
        teardown();
      }
    };
    return pc;
  }, [dispatch, teardown]);

  const answerIncoming = useCallback(async (callId) => {
    const pending = callId ? pendingIncomingCallsRef.current.get(callId) : null;
    if (!pending) return;
    // One RTCPeerConnection per tab — there is no hardware concept of being
    // on two calls at once. Answering a second call while already
    // active/connecting on another would silently steal pcRef out from under
    // the first one instead of actually supporting two live calls.
    if (pcRef.current || activeCallIdRef.current) {
      dispatch(callError("Finish or hang up your current call before answering another"));
      return;
    }
    try {
      if (pending.bridgeMode === "sfu") {
        // Server negotiates WebRTC with Meta itself in this mode — no local
        // RTCPeerConnection, no offerSdp to answer. POST /answer alone makes
        // the server generate its SDP answer and tell Meta accept; only once
        // that's actually confirmed does this tab join the mediasoup Router,
        // same "don't touch media before accept is confirmed" ordering as
        // the direct-mode path below, just enforced server-side instead of
        // via a muted-track trick.
        dispatch(
          callStarted({
            callId: pending.callId,
            wacid: pending.wacid,
            direction: "inbound",
            remoteNumber: pending.from,
            contactId: pending.contactId || null,
            contactName: pending.contactName || null,
            bridgeMode: "sfu",
          })
        );
        activeCallIdRef.current = pending.callId;

        await authPost("/api/calling/answer", { callId: pending.callId });
        await joinSfuCall(pending.callId);
        dispatch(callConnected());
        navigateToContactChat(pending.contactId);
      } else {
        const localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Muted until Meta's accept 200 OK — flowing media too early clips the
        // first words the caller hears (Meta's documented behavior).
        localStream.getAudioTracks().forEach((t) => (t.enabled = false));
        localStreamRef.current = localStream;

        const pc = newPeerConnection();
        pcRef.current = pc;
        localStream.getTracks().forEach((t) => pc.addTrack(t, localStream));

        await pc.setRemoteDescription({ type: "offer", sdp: pending.offerSdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await waitForIceGatheringComplete(pc);

        dispatch(
          callStarted({
            callId: pending.callId,
            wacid: pending.wacid,
            direction: "inbound",
            remoteNumber: pending.from,
            contactId: pending.contactId || null,
            contactName: pending.contactName || null,
            bridgeMode: "direct",
          })
        );
        activeCallIdRef.current = pending.callId;

        await authPost("/api/calling/answer", { callId: pending.callId, answerSdp: pc.localDescription.sdp });
        localStream.getAudioTracks().forEach((t) => (t.enabled = true));
        dispatch(callConnected());
        navigateToContactChat(pending.contactId);
      }
    } catch (err) {
      dispatch(callError(describeCallError(err)));
      dispatch(clearIncoming(pending.callId));
      // Scoped to this callId — the double-answer guard above already
      // guarantees no unrelated call could have become active mid-attempt,
      // so this can never clobber a different call's state.
      dispatch(callEnded(pending.callId));
      teardown();
    } finally {
      pendingIncomingCallsRef.current.delete(pending.callId);
      dispatch(unmarkAnswerable(pending.callId));
    }
  }, [dispatch, newPeerConnection, teardown, navigateToContactChat, joinSfuCall]);

  const rejectIncoming = useCallback(
    async (callId) => {
      const pending = callId ? pendingIncomingCallsRef.current.get(callId) : null;
      if (!pending) return;
      pendingIncomingCallsRef.current.delete(callId);
      dispatch(clearIncoming(callId));
      dispatch(unmarkAnswerable(callId));
      try {
        await authPost("/api/calling/reject", { callId: pending.callId });
      } catch (err) {
        // best-effort — Meta times the call out regardless if this fails
      }
    },
    [dispatch]
  );

  const hangup = useCallback(
    async (callId) => {
      if (!callId) return;
      try {
        await authPost("/api/calling/hangup", { callId });
      } catch (err) {
        // best-effort — tear down locally either way
      }
      dispatch(callEnded(callId));
      teardown();
    },
    [dispatch, teardown]
  );

  /**
   * "sfu-auto" mode only — the call already connected to Meta the instant it
   * rang, with no agent yet, so there's nothing to answer/accept here, just
   * a live mediasoup Router to join. Called from the Calls sidebar's "Join"
   * action (see LiveCallCard/CallsSectionBody) on a call that's already
   * status "connected" but has no agentId.
   */
  const joinLiveCall = useCallback(
    async ({ callId, contactId, contactName, waNumber, connectedAt }) => {
      if (pcRef.current || activeCallIdRef.current) {
        dispatch(callError("Finish or hang up your current call before joining another"));
        return;
      }
      try {
        dispatch(
          callStarted({ callId, wacid: null, direction: "inbound", remoteNumber: waNumber, contactId: contactId || null, contactName: contactName || null, bridgeMode: "sfu" })
        );
        activeCallIdRef.current = callId;
        await joinSfuCall(callId);
        // Pass the call's real connect time (from when it auto-accepted,
        // possibly minutes before this agent clicked Join) rather than
        // defaulting to "now" — otherwise the widget's timer would restart
        // from zero at join time instead of showing true elapsed duration.
        dispatch(callConnected({ connectedAt }));
        navigateToContactChat(contactId);
      } catch (err) {
        dispatch(callError(describeCallError(err)));
        dispatch(callEnded(callId));
        teardown();
      }
    },
    [dispatch, joinSfuCall, teardown, navigateToContactChat]
  );

  /**
   * "Open call" recovery action from the Calls sidebar — for a call that's
   * genuinely still ringing in the DB but never reached this tab's widget
   * (missed push, opened after the ring started, ring-group call answered
   * from a tab other than the one it first landed on). Fetches the same
   * payload shape the original call:incoming socket push would have carried
   * and feeds it through the identical pending-call + answerable wiring, so
   * Accept/Reject behave exactly as if the push had just arrived — this is
   * the client-side reconciliation counterpart to the backend's stuck-ringing
   * cleanup (see server/utils/callingReconciliationQueue.js): that handles
   * calls nobody ever answers, this handles calls an agent legitimately
   * wants to answer but whose live push never reached them.
   */
  const openRingingCall = useCallback(
    async (callId) => {
      if (!callId) return;
      try {
        const payload = await authGet(`/api/calling/${callId}/ring-state`);
        pendingIncomingCallsRef.current.set(payload.callId, payload);
        dispatch(markAnswerable(payload.callId));
        // focusIncomingCall (not callIncoming) is unconditional — this is a
        // deliberate click on a specific call, not a race for the widget slot.
        dispatch(focusIncomingCall(payload));
      } catch (err) {
        dispatch(callError(err.response?.data?.error || describeCallError(err)));
      }
    },
    [dispatch]
  );

  /** Optimistic: shows "Transferring to X…" immediately, corrected if the request itself fails outright (the async accept/decline/timeout outcomes arrive later via socket, not this response). */
  const initiateTransfer = useCallback(
    async (callId, toAgentId, toAgentName) => {
      dispatch(transferPendingStarted({ callId, toAgentId, toAgentName }));
      try {
        await authPost("/api/calling/transfer/initiate", { callId, toAgentId });
      } catch (err) {
        dispatch(transferPendingCleared(callId));
        dispatch(callError(describeCallError(err)));
      }
    },
    [dispatch]
  );

  const cancelTransfer = useCallback(
    async (callId) => {
      dispatch(transferPendingCleared(callId)); // optimistic — the server-side rollback happens regardless
      try {
        await authPost("/api/calling/transfer/cancel", { callId });
      } catch (err) {
        // best-effort — if this somehow fails, the 30s server-side timeout still resolves it
      }
    },
    [dispatch]
  );

  /**
   * transfer is the full call:transferRequest payload from Redux (callId,
   * wacid, contactId, contactName, waNumber) — this agent never received the
   * original offer SDP (there isn't one, SFU-only feature) or a `pending`
   * entry the way a normal incoming call does, so everything needed has to
   * come from that payload directly.
   */
  const respondToTransfer = useCallback(
    async (transfer, accept) => {
      if (!transfer) return;
      dispatch(clearIncomingTransfer(transfer.callId));

      if (accept && (pcRef.current || activeCallIdRef.current)) {
        dispatch(callError("Finish or hang up your current call before accepting a transfer"));
        authPost("/api/calling/transfer/respond", { callId: transfer.callId, accept: false }).catch(() => {});
        return;
      }
      try {
        await authPost("/api/calling/transfer/respond", { callId: transfer.callId, accept });
        if (!accept) return;

        dispatch(
          callStarted({
            callId: transfer.callId,
            wacid: transfer.wacid,
            direction: "inbound",
            remoteNumber: transfer.waNumber,
            contactId: transfer.contactId || null,
            contactName: transfer.contactName || null,
            bridgeMode: "sfu",
          })
        );
        activeCallIdRef.current = transfer.callId;
        await joinSfuCall(transfer.callId);
        dispatch(callConnected());
        navigateToContactChat(transfer.contactId);
      } catch (err) {
        dispatch(callError(describeCallError(err)));
        if (accept) {
          dispatch(callEnded(transfer.callId));
          teardown();
        }
      }
    },
    [dispatch, joinSfuCall, teardown, navigateToContactChat]
  );

  // Called once on mount (see CallManager) to recover from a page refresh —
  // Redux state (the incoming popup) doesn't survive a reload on its own.
  const recoverActiveCall = useCallback(async () => {
    try {
      const { ringing, staleConnected } = await authGet("/api/calling/active");
      if (ringing) {
        pendingIncomingCallsRef.current.set(ringing.callId, ringing);
        dispatch(callIncoming(ringing));
        dispatch(markAnswerable(ringing.callId));
      }
      if (staleConnected) {
        // The RTCPeerConnection died with the reload — there is no "resume"
        // for WebRTC state. A clean hangup is the honest outcome here, not
        // a silently dead call the customer is still sitting on.
        await authPost("/api/calling/hangup", { callId: staleConnected.callId }).catch(() => {});
      }
    } catch (err) {
      // best-effort — worst case, the agent doesn't see a call that's still
      // legitimately ringing until the next live socket event for it
    }
  }, [dispatch]);

  const dialOut = useCallback(
    async ({ to, contactId, contactName }) => {
      try {
        // Fetched fresh every dial, never cached — same reasoning as
        // useCallPermission's "always live" pattern: this is a workspace
        // setting that can change between calls, and getting it wrong here
        // means either negotiating an offer the server will ignore (sfu
        // mode) or trying to server-negotiate with no local offer to fall
        // back to (direct mode).
        const { bridgeMode } = await authGet("/api/calling/bridge-mode");

        if (bridgeMode !== "direct") {
          // Server generates its own offer and negotiates with Meta itself
          // — mirrors the inbound sfu-ring accept path. Media only joins
          // once Meta actually answers (see onAnswered's sfu branch below),
          // not here.
          const { callId, wacid } = await authPost("/api/calling/dial", { to, contactId });
          dispatch(
            callStarted({ callId, wacid, direction: "outbound", remoteNumber: to, contactId, contactName: contactName || null, bridgeMode: "sfu" })
          );
          activeCallIdRef.current = callId;
          return;
        }

        const localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        localStream.getAudioTracks().forEach((t) => (t.enabled = false));
        localStreamRef.current = localStream;

        const pc = newPeerConnection();
        pcRef.current = pc;
        localStream.getTracks().forEach((t) => pc.addTrack(t, localStream));

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await waitForIceGatheringComplete(pc);

        const { callId, wacid } = await authPost("/api/calling/dial", { to, contactId, offerSdp: pc.localDescription.sdp });
        dispatch(
          callStarted({ callId, wacid, direction: "outbound", remoteNumber: to, contactId, contactName: contactName || null, bridgeMode: "direct" })
        );
        activeCallIdRef.current = callId;
      } catch (err) {
        const message =
          err.response?.data?.code === "NO_CALL_PERMISSION" ? "No call permission for this contact yet" : describeCallError(err);
        dispatch(callError(message));
        teardown();
      }
    },
    [dispatch, newPeerConnection, teardown]
  );

  useEffect(() => {
    const onIncoming = (payload) => {
      pendingIncomingCallsRef.current.set(payload.callId, payload);
      dispatch(callIncoming(payload));
      dispatch(markAnswerable(payload.callId));
    };
    const onAnswered = async (payload) => {
      // Only meaningful for the call this tab actually dialed — a different
      // outbound call elsewhere in the workspace connecting must not touch
      // this tab's own state.
      if (payload.callId !== activeCallIdRef.current) return;
      try {
        if (payload.bridgeMode === "sfu") {
          // The server already applied Meta's answer to its own werift peer
          // (calling.webhook.js) — this tab never negotiated anything
          // itself, it just joins the mediasoup room now that Meta has
          // actually picked up, same as accepting an inbound sfu-ring call.
          await joinSfuCall(payload.callId);
          dispatch(callConnected());
          return;
        }
        if (!pcRef.current) return;
        await pcRef.current.setRemoteDescription({ type: "answer", sdp: payload.answerSdp });
        if (localStreamRef.current) localStreamRef.current.getAudioTracks().forEach((t) => (t.enabled = true));
        dispatch(callConnected());
      } catch (err) {
        dispatch(callError(err.message));
      }
    };
    const onStatus = (payload) => dispatch(callStatusUpdate(payload));
    // Any call ending anywhere in the workspace comes through here — not
    // just this tab's own. callEnded/clearIncoming are callId-scoped so an
    // unrelated call timing out never touches this tab's real active call;
    // teardown (which kills the live RTCPeerConnection) only runs when the
    // call that ended actually is this tab's own.
    const onEnded = (payload) => {
      const endedCallId = payload?.callId;
      pendingIncomingCallsRef.current.delete(endedCallId);
      dispatch(clearIncoming(endedCallId));
      dispatch(unmarkAnswerable(endedCallId));
      dispatch(callEnded(endedCallId));
      if (endedCallId && endedCallId === activeCallIdRef.current) teardown();
    };
    const onRequestDial = (payload) => dialOut(payload);
    // Someone else answered/declined/failed-to-answer the same ring — stand
    // down for that specific call only. Safe to fire even for this agent's
    // own claim: clearing an already-cleared incoming call is a no-op.
    const onClaimed = (payload) => {
      pendingIncomingCallsRef.current.delete(payload.callId);
      dispatch(clearIncoming(payload.callId));
      dispatch(unmarkAnswerable(payload.callId));
    };
    // A sequential/smart ring strategy moved on from this agent (their turn timed out, or the
    // call was passed on). Dismiss THIS agent's popup only — the call itself is still ringing for
    // someone else, so unlike onClaimed nothing here may touch the shared live-calls list.
    const onRingReleased = (payload) => {
      pendingIncomingCallsRef.current.delete(payload.callId);
      dispatch(clearIncoming(payload.callId));
      dispatch(unmarkAnswerable(payload.callId));
    };
    // From the Calls sidebar's live-call cards — any ringing call can be
    // answered/rejected from there, not just the one shown in the widget.
    const onRequestAnswer = (payload) => answerIncoming(payload.callId);
    const onRequestReject = (payload) => rejectIncoming(payload.callId);
    // sfu-auto calls only — a live call with no agent yet, joined straight
    // from its own "Join" action rather than an accept/reject popup.
    const onRequestJoin = (payload) => joinLiveCall(payload);
    // "Open call" button on a ringing sidebar card this tab never got the
    // live push for — see openRingingCall's own doc comment above.
    const onRequestOpenRinging = (payload) => openRingingCall(payload.callId);
    // Direct patch of the chatbox row — see calling.webhook.js's
    // updateCallChatEntryOnEnd for why this is a dedicated event instead of
    // being folded into the general message pipeline.
    const onLogUpdated = (payload) => {
      dispatch(patchChatMessage({ messageId: payload.messageId, patch: payload.patch }));
    };
    // This agent was picked as a transfer target — same "ring" treatment as
    // a real incoming call, rendered by a separate widget (see CallManager).
    const onTransferRequest = (payload) => dispatch(transferIncoming(payload));
    // Reaches whichever side needs it: the initiator (their "Transferring to
    // X…" row needs to clear) and/or the target (their popup needs to
    // dismiss) — see callingTransferController.rollbackTransfer for exactly
    // who gets this and when. Both dispatches are no-ops if they don't apply
    // to this tab (callId won't match either piece of state).
    const onTransferCancelled = (payload) => {
      dispatch(clearIncomingTransfer(payload.callId));
      dispatch(transferPendingCleared(payload.callId));
    };
    const onTransferDeclined = (payload) => {
      dispatch(transferPendingCleared(payload.callId));
      dispatch(callError("The other agent declined the transfer"));
    };
    // Workspace-wide — only the initiator's own tab needs to actually tear
    // its call down (the call is no longer theirs); everyone else just needed
    // this for the live-calls list (handled separately, useLiveCallsFeed).
    const onTransferCompleted = (payload) => {
      dispatch(transferPendingCleared(payload.callId));
      const user = authUserRef.current;
      const myUserId = user?._id || user?.userId;
      if (payload.callId === activeCallIdRef.current && String(payload.newAgentId) !== String(myUserId)) {
        dispatch(callEnded(payload.callId));
        teardown();
      }
    };

    connectionMonitor.on("call:incoming", onIncoming);
    connectionMonitor.on("call:answered", onAnswered);
    connectionMonitor.on("call:status", onStatus);
    connectionMonitor.on("call:ended", onEnded);
    connectionMonitor.on("call:requestDial", onRequestDial);
    connectionMonitor.on("call:requestAnswer", onRequestAnswer);
    connectionMonitor.on("call:requestReject", onRequestReject);
    connectionMonitor.on("call:requestJoin", onRequestJoin);
    connectionMonitor.on("call:requestOpenRinging", onRequestOpenRinging);
    connectionMonitor.on("call:claimed", onClaimed);
    connectionMonitor.on("call:ring_released", onRingReleased);
    connectionMonitor.on("call:log_updated", onLogUpdated);
    connectionMonitor.on("call:transferRequest", onTransferRequest);
    connectionMonitor.on("call:transferCancelled", onTransferCancelled);
    connectionMonitor.on("call:transferDeclined", onTransferDeclined);
    connectionMonitor.on("call:transferCompleted", onTransferCompleted);

    return () => {
      connectionMonitor.off("call:incoming", onIncoming);
      connectionMonitor.off("call:answered", onAnswered);
      connectionMonitor.off("call:status", onStatus);
      connectionMonitor.off("call:ended", onEnded);
      connectionMonitor.off("call:requestDial", onRequestDial);
      connectionMonitor.off("call:requestAnswer", onRequestAnswer);
      connectionMonitor.off("call:requestReject", onRequestReject);
      connectionMonitor.off("call:requestJoin", onRequestJoin);
      connectionMonitor.off("call:requestOpenRinging", onRequestOpenRinging);
      connectionMonitor.off("call:claimed", onClaimed);
      connectionMonitor.off("call:ring_released", onRingReleased);
      connectionMonitor.off("call:log_updated", onLogUpdated);
      connectionMonitor.off("call:transferRequest", onTransferRequest);
      connectionMonitor.off("call:transferCancelled", onTransferCancelled);
      connectionMonitor.off("call:transferDeclined", onTransferDeclined);
      connectionMonitor.off("call:transferCompleted", onTransferCompleted);
    };
  }, [dispatch, dialOut, teardown, answerIncoming, rejectIncoming, joinLiveCall, joinSfuCall, openRingingCall]);

  // A reload doesn't just lose Redux state, it kills the actual
  // RTCPeerConnection — there is no reconnecting to the same media session.
  // Best-effort tell Meta the call is over instead of leaving it dangling
  // for however long Meta's own timeout takes; getActiveCall's
  // staleConnected check on the *next* load is the backstop if this
  // particular request doesn't make it out in time.
  useEffect(() => {
    const handleUnload = () => {
      if (activeCallIdRef.current) beaconHangup(activeCallIdRef.current);
    };
    window.addEventListener("pagehide", handleUnload);
    return () => window.removeEventListener("pagehide", handleUnload);
  }, []);

  // Covers the case pagehide doesn't: this hook's owner (CallManager, mounted
  // near the app root — see its own render guard) unmounting while the tab
  // itself stays open, e.g. `isAuthenticated` flipping false on logout or a
  // forced session end mid-call. Without this, the live RTCPeerConnection/
  // localStream/mediasoup transports are simply abandoned — nothing ever
  // calls teardown() again, so the mic stays hot and the call sits
  // "connected" server-side until the 6h reconciliation safety net
  // (scheduleConnectedTimeoutCheck) eventually resolves it. teardown() itself
  // is a stable useCallback (its only dep, leaveSfuCall, never changes
  // identity — see useSfuCallConnection), so this effect's cleanup only ever
  // fires on the real unmount, not on incidental re-renders.
  useEffect(() => {
    return () => {
      const callId = activeCallIdRef.current;
      if (!callId) return;
      // Local teardown is what actually stops the mic track and closes the
      // peer connection / mediasoup transports — it must happen
      // synchronously and unconditionally here, not depend on the network
      // call below succeeding.
      teardown();
      // Best-effort tell Meta too, same as beaconHangup's reasoning — by the
      // time this runs the auth token may already be gone (this is often
      // firing *because* of a logout), so this is allowed to fail silently;
      // Meta's own timeout and the reconciliation safety net are still there
      // as a backstop either way.
      authPost("/api/calling/hangup", { callId }).catch(() => {});
    };
  }, [teardown]);

  return { answerIncoming, rejectIncoming, hangup, recoverActiveCall, joinLiveCall, initiateTransfer, cancelTransfer, respondToTransfer };
}
